import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ShippingSelector, useShippingMethods, RELAY_METHODS } from "@/components/site/ShippingSelector";
import { CustomerForm, EMPTY_CUSTOMER, customerErrors } from "@/components/checkout/CustomerForm";
import { PaymentMethods, usePaymentMethods } from "@/components/checkout/PaymentMethods";
import { PayPalPay } from "@/components/checkout/PayPalPay";
import { InstantTransferNote } from "@/components/checkout/BankBox";
import { PromoField } from "@/components/checkout/PromoField";
import { useSeo } from "@/lib/seo";
import { SecureNote } from "@/components/site/Trust";
import { landingRef, trackStep } from "@/components/site/VisitTracker";
import { BirthdayField } from "@/components/site/BirthdayField";
import { rememberEmail, saveBirthday } from "@/lib/knownEmail";
import { PregnancyNotice, useCartHasRepouss } from "@/components/site/PregnancyNotice";
import { T, useLang, Approx, shopPath, useItemName } from "@/lib/i18n";
import { SubCartOffer } from "@/components/subscription/SubPromo";
import { PreorderCartNotice } from "@/components/site/Preorder";

const DOMTOM_ZIP = /^9(7[1-8]|8[6-8])/;

const cartPayload = (items) => items.map((i) => (i.box ? { slug: "coffret-personnalise", quantity: i.quantity, box: i.box.map((b) => ({ slug: b.slug, qty: b.qty })) } : { slug: i.slug, quantity: i.quantity }));

const names = {};
const countryName = (c, lang = "fr") => {
  try { names[lang] ||= new Intl.DisplayNames([lang], { type: "region" }); return names[lang].of(c) || c; } catch { return c; }
};

function CountrySelect({ countries, value, onChange }) {
  const { lang } = useLang();
  const list = [...(countries || [])].sort((a, b) => countryName(a, lang).localeCompare(countryName(b, lang), lang));
  return (
    <label className="mt-3 block text-xs text-[var(--parchment)]/85"><T>Pays de livraison</T>
      <select data-testid="checkout-country-select" value={list.includes(value) ? value : ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-12 w-full rounded-sm border border-[var(--line)] bg-[var(--ink)] px-3 text-base text-[var(--parchment)] outline-none focus:border-[var(--gold)]">
        <option value=""><T>Choisir le pays…</T></option>
        {list.map((c) => <option key={c} value={c}>{countryName(c, lang)}</option>)}
      </select>
    </label>
  );
}

function postToMonetico(action, fields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields || {}).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value ?? "");
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

function Summary({ items, subtotal, shipCost, total, promo }) {
  const { lang } = useLang();
  const nm = useItemName();
  return (
    <aside data-testid="checkout-summary" className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
      <p className="eyebrow mb-4"><T>Récapitulatif</T></p>
      {items.map((i) => (
        <div key={i.slug} className="flex justify-between gap-4 border-b border-[var(--line)] py-3 text-sm">
          <span>{i.quantity} × {nm(i)}</span>
          <span className="shrink-0 text-[var(--gold-soft)]">{euro(i.price * i.quantity)}</span>
        </div>
      ))}
      {lang === "en" ? null : <SubCartOffer items={items} placement="checkout" />}
      <PreorderCartNotice items={items} testid="checkout-preorder-notice" />
      <div className="mt-4 flex justify-between text-sm text-[var(--muted)]"><span><T>Sous-total</T></span><span data-testid="checkout-subtotal">{euro(subtotal)}</span></div>
      {promo ? <div className="mt-1 flex justify-between text-sm text-[var(--gold-soft)]"><span><T>Code</T> {promo.code}</span><span data-testid="checkout-discount">-{euro(promo.discount)}</span></div> : null}
      <div className="mt-1 flex justify-between text-sm text-[var(--muted)]"><span><T>Livraison</T></span><span data-testid="checkout-shipping">{shipCost ? euro(shipCost) : <T>Gratuite</T>}</span></div>
      <div className="mt-4 flex items-baseline justify-between"><span className="text-sm"><T>Total TTC</T></span><span data-testid="checkout-total" className="font-display text-3xl">{euro(total)}</span></div>
      <div className="text-right"><Approx eur={total} /></div>
      <p className="mt-2 text-xs text-[var(--parchment)]/75"><T>Total définitif, livraison incluse : aucun frais caché.</T></p>
      {lang === "en" ? <p data-testid="checkout-currency-note" className="mt-2 text-xs leading-relaxed text-[var(--parchment)]/75"><T>Prix facturés en euros. Les montants en £ et $ sont des estimations indicatives (taux du jour, approximatif). Hors Union européenne, des droits ou taxes d'importation locaux peuvent s'appliquer.</T></p> : null}
    </aside>
  );
}

export default function Checkout() {
  const nav = useNavigate();
  const { state } = useLocation();
  const { items, total: subtotal, clear } = useCart();
  const { lang, t } = useLang();
  const repouss = useCartHasRepouss(items);
  const [method, setMethod] = useState(state?.method || "mondial_relay");
  const [relay, setRelay] = useState(state?.relay || null);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [birthday, setBirthday] = useState({ day: "", month: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [pay, setPay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [promo, setPromo] = useState(null);
  const { data: shipMethods } = useShippingMethods(subtotal);
  const { data: payData, isLoading: payLoading } = usePaymentMethods();
  const payMethods = Array.isArray(payData) ? payData : [];
  const available = payMethods.filter((m) => m.available);
  const selected = available.find((m) => m.id === pay) || available[0];
  const shipList = Array.isArray(shipMethods) ? shipMethods : [];
  const shipCost = shipList.find((m) => m.id === method)?.amount ?? 0;
  const world = ["colissimo_world", "colissimo_uk_ch"].includes(method);
  const isRelay = RELAY_METHODS.includes(method);
  const total = Math.round((subtotal - (promo?.discount ?? 0) + shipCost) * 100) / 100;
  const errors = customerErrors(customer);
  const needRelay = isRelay && !relay;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idemKey = useMemo(() => crypto.randomUUID(), [selected?.id, total, method, relay?.id, attempt, promo?.code]);

  useEffect(() => {
    const reset = (e) => { if (e.persisted) setLoading(false); };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  useEffect(() => { setPromo(null); }, [subtotal]);
  const hasItems = items.length > 0;
  useEffect(() => { if (hasItems) trackStep("checkout"); }, [hasItems]);
  useEffect(() => { if (!world) setCustomer((c) => (c.country === "FR" ? c : { ...c, country: "FR" })); }, [world]);
  useSeo({ title: lang === "en" ? "Complete my order | Ozentya" : "Finaliser ma commande | Ozentya", noindex: true });

  const validate = () => {
    setShowErrors(true);
    if (needRelay) { toast.error(t("Choisissez votre Point Relais ou Locker.")); document.querySelector('[data-testid="shipping-choose-relay"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return false; }
    if (Object.keys(errors).length) { toast.error(t("Merci de compléter vos coordonnées.")); return false; }
    const domtom = DOMTOM_ZIP.test(customer.zip.trim());
    if (world && (!customer.country || customer.country === "FR")) { toast.error(t("Choisissez votre pays de livraison.")); return false; }
    if (method === "colissimo_domtom" && !domtom) { toast.error(t("Le tarif Outre-mer est réservé aux adresses des DOM-TOM (codes postaux 971 à 988).")); return false; }
    if (method === "colissimo" && domtom) { toast.error(t("Pour une adresse en Outre-mer, choisissez « Colissimo Outre-mer (DOM-TOM) ».")); return false; }
    rememberEmail(customer.email);
    saveBirthday(customer.email, birthday.day, birthday.month, "commande");
    return true;
  };

  const startOrder = async () => {
    try {
      const { data } = await api.post("/orders/checkout", {
        items: cartPayload(items), origin_url: window.location.origin, shipping_method: method,
        relay: isRelay ? relay : null, customer, payment_method: selected.id, idempotency_key: idemKey, promo_code: promo?.code, visit_ref: landingRef(), lang,
      });
      return data;
    } catch (e) {
      toast.error(t(errMsg(e)));
      throw e;
    }
  };

  const payRedirect = async () => {
    if (!validate() || loading) return;
    setLoading(true);
    try {
      if (selected.id === "stripe") {
        const { data } = await api.post("/payments/checkout", { items: cartPayload(items), origin_url: window.location.origin, shipping_method: method, relay: isRelay ? relay : null, promo_code: promo?.code, customer, visit_ref: landingRef(), lang });
        if (data?.checkout_url) window.location.href = data.checkout_url;
        return;
      }
      const data = await startOrder();
      if (data?.provider === "reservation") {
        clear();
        nav(`/payment/success?ref=${data.reference}`);
        return;
      }
      postToMonetico(data?.action, data?.fields);
    } catch (e) {
      if (selected.id === "stripe") toast.error(t(errMsg(e)));
      setLoading(false);
      setAttempt((a) => a + 1);
    }
  };

  if (!items.length) {
    return (
      <main data-testid="checkout-empty" className="flex min-h-screen flex-col items-center justify-center px-5 pt-32 text-center">
        <h1 className="font-display text-4xl italic md:text-5xl"><T>Votre panier est vide.</T></h1>
        <Link to={shopPath(lang)} data-testid="checkout-back-shop" className="mt-10 flex h-14 items-center rounded-full bg-[var(--gold)] px-8 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--ink)]"><T>Découvrir la boutique</T></Link>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="mx-auto max-w-6xl px-5 pb-24 pt-40 md:px-10">
      <p className="eyebrow mb-4"><T>Commande</T></p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl"><T>Finaliser</T> <span className="italic gold-text"><T>ma commande</T></span></h1>
      <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_380px]">
        <div className="space-y-12">
          <section><p className="eyebrow mb-4"><T>Vos coordonnées</T></p><CustomerForm value={customer} onChange={setCustomer} errors={errors} showErrors={showErrors} />
            <div className="mt-6"><BirthdayField value={birthday} onChange={setBirthday} testid="checkout-birthday" />
              <p className="mt-2 text-xs text-[var(--parchment)]/70"><T>En indiquant cette date, vous acceptez de recevoir chaque année votre code d'anniversaire par email (désinscription en un clic).</T></p></div></section>
          <section><ShippingSelector method={method} setMethod={setMethod} relay={relay} setRelay={setRelay} subtotal={subtotal} />
            {world ? <CountrySelect countries={shipList.find((m) => m.id === method)?.countries} value={customer.country} onChange={(country) => setCustomer((c) => ({ ...c, country }))} /> : null}</section>
          <section>
            <p className="eyebrow mb-4"><T>Choisissez votre moyen de paiement</T></p>
            {payLoading ? <Loader2 className="h-5 w-5 animate-spin text-[var(--gold)]" /> : null}
            {!payLoading && !available.length ? <p data-testid="payment-unavailable" className="mb-4 rounded-sm border border-[var(--line)] p-4 text-sm text-[var(--parchment)]/90"><T>Le paiement en ligne est momentanément indisponible. Contactez-nous pour finaliser votre commande.</T></p> : null}
            <PaymentMethods methods={available.length ? available : payMethods} value={selected?.id} onChange={setPay} />
            {selected?.id === "reservation" ? <InstantTransferNote className="mt-4" testid="checkout-instant-transfer-note" /> : null}
          </section>
        </div>
        <div className="space-y-4 lg:sticky lg:top-36 lg:self-start">
          <Summary items={items} subtotal={subtotal} shipCost={shipCost} total={total} promo={promo} />
          <PromoField items={cartPayload(items)} promo={promo} setPromo={setPromo} email={customer.email} />
          {selected ? <p data-testid="checkout-selected-method" className="text-xs text-[var(--muted)]"><T>Moyen de paiement : </T><span className="text-[var(--parchment)]">{t(selected.id === "paypal" ? "PayPal" : selected.id === "reservation" ? "Virement bancaire" : "Carte bancaire")}</span></p> : null}
          {repouss ? <PregnancyNotice testid="checkout-pregnancy-notice" className="mt-0" /> : null}
          {selected?.id === "paypal" ? (
            <PayPalPay clientId={selected.client_id} amount={total} disabled={false} validate={validate} startOrder={startOrder}
              onPaid={(ref) => { clear(); nav(`/payment/success?ref=${ref}`); }} onReset={() => setAttempt((a) => a + 1)} />
          ) : (
            <button data-testid="checkout-pay-button" disabled={!selected || loading} onClick={payRedirect}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-sm font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:bg-[var(--gold-soft)] active:scale-[0.98] disabled:opacity-40">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t(selected?.id === "reservation" ? "Enregistrement…" : "Redirection sécurisée…")}</> : `${t(selected?.id === "reservation" ? "Réserver ma commande" : "Payer")} ${euro(total)}`}
            </button>
          )}
          <SecureNote testid="checkout-secure-note" />
          <p className="text-xs text-[var(--parchment)]/70"><T>En validant, vous acceptez nos </T><Link to="/cgv" data-testid="checkout-cgv-link" className="underline underline-offset-4"><T>conditions générales de vente</T></Link>.</p>
          <p data-testid="checkout-followup-note" className="text-xs text-[var(--parchment)]/70"><T>Après votre commande, vous recevrez quelques emails de conseils et de suivi sur vos soins. Désinscription en un clic dans chaque email (</T><Link to="/confidentialite" className="underline underline-offset-4"><T>confidentialité</T></Link>).</p>
        </div>
      </div>
    </main>
  );
}
